import type {
  MessageNode,
  ModerationKind,
  ModerationPriority,
  ModerationStatus,
} from '../types';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export interface BadgeDisplay {
  label: string;
  variant: BadgeVariant;
}

/** ISO-8601 -> locale date+time, safe on empty/invalid input. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** ISO-8601 -> locale date only. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** PENDING -> Pending, DISMISSED -> Completed (the report is marked COMPLETED on dismiss). */
export function statusDisplay(status: ModerationStatus): BadgeDisplay {
  return status === 'DISMISSED'
    ? { label: 'Completed', variant: 'default' }
    : { label: 'Pending', variant: 'secondary' };
}

export function priorityDisplay(priority: ModerationPriority): BadgeDisplay {
  switch (priority) {
    case 'CRITICAL':
      return { label: 'Critical', variant: 'destructive' };
    case 'HIGH':
      return { label: 'High', variant: 'destructive' };
    case 'MEDIUM':
      return { label: 'Medium', variant: 'secondary' };
    case 'LOW':
    default:
      return { label: 'Low', variant: 'outline' };
  }
}

const KIND_LABEL: Record<ModerationKind, string> = {
  USER: 'User',
  CONTENT: 'Content',
  COMMENT: 'Comment',
  MESSAGE: 'Message',
  FEEDBACK: 'Feedback',
};

export function kindLabel(kind: ModerationKind): string {
  return KIND_LABEL[kind];
}

/** True iff the post is currently hidden (status HID / HIDDEN) — drives the Take-Down / List toggle. */
export function isContentHidden(status: string | null | undefined): boolean {
  return ['HID', 'HIDDEN'].includes((status ?? '').trim().toUpperCase());
}

/** True iff the post was permanently removed by an admin (status TER / TERMINATED) — terminal, no undo. */
export function isContentRemoved(status: string | null | undefined): boolean {
  return ['TER', 'TERMINATED'].includes((status ?? '').trim().toUpperCase());
}

/** First-letter avatar fallback. */
export function initials(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  return n ? n.charAt(0).toUpperCase() : 'U';
}

/**
 * Message display text (parity ModerationDetailsViewModel.kt:208-216):
 * TIP -> "Sent a Tip"; POST/EXCLUSIVE/SHARE/COLLABORATOR/STORY -> "Post ID: N"; else the raw content.
 */
export function messageDisplayText(node: MessageNode): string {
  const t = (node.contentType ?? '').trim().toUpperCase();
  if (t === 'TIP') return '💸 Sent a Tip';
  if (['POST', 'EXCLUSIVE', 'SHARE', 'COLLABORATOR', 'STORY'].includes(t)) {
    return `📎 Post ID: ${node.postId ?? '—'}`;
  }
  return node.text || '—';
}
